import { createContext, useContext, useEffect, useMemo, useState } from "react";
import { CAMPUSES, DEFAULT_CAMPUS, isValidCampus } from "../config/campuses";
import { createPatientService } from "../services/patientService";

/**
 * `TenantContext` is the v2 successor to v1's TenantContext. It still exposes:
 *   - `tenant`       — the campus descriptor `{ id, label, features }`
 *   - `patientService` — domain fetchers (NestJS backend)
 *   - `campus`       — the slug, also available as `tenant.id`
 *   - `changeCampus` — switch tenant
 *
 * v1's `db` (Firestore instance) is replaced by `campus` — the generatedNotePresence helpers
 * now take the campus slug instead.
 *
 * The `features` map mirrors v1 closely so existing UI conditionals work unchanged:
 *   - `generateNotesAlliedSessionTabs` — show 5 allied session tabs (mindtalk only)
 *   - `consultationFilterSubcampusLabels` — relabel "Campus" → "Subcampus" (mindtalk only)
 *   - `hrEmployeeDoctorLists` — `"all_roles"` (mindtalk) | `"psych_only"` (others)
 *   - `displayTimeZone` — passed to `toLocaleString` for table dates
 *   - `defaultGeneratedNoteOrderField` — informational; v2 sorts client-side by `date`
 *   - `normalizeGeneratedNotes` — kept for compat (no-op in v2)
 */

const TENANT_FEATURES = {
  mindtalk: {
    generateNotesAlliedSessionTabs: true,
    consultationFilterSubcampusLabels: true,
    hrEmployeeDoctorLists: "all_roles",
    displayTimeZone: "Asia/Kolkata",
    normalizeGeneratedNotes: true,
    defaultGeneratedNoteOrderField: "updated",
  },
  cadabams: {
    hrEmployeeDoctorLists: "psych_only",
    displayTimeZone: "Asia/Kolkata",
    normalizeGeneratedNotes: true,
    defaultGeneratedNoteOrderField: "updated",
  },
  mysore: {
    hrEmployeeDoctorLists: "psych_only",
    displayTimeZone: "Asia/Kolkata",
    normalizeGeneratedNotes: true,
  },
  whitefield: {
    hrEmployeeDoctorLists: "psych_only",
    displayTimeZone: "Asia/Kolkata",
    normalizeGeneratedNotes: true,
    defaultGeneratedNoteOrderField: "updated",
  },
};

const STORAGE_KEY = "uda2.campus";

const TenantContext = createContext(null);

export function TenantProvider({ children }) {
  const [slug, setSlug] = useState(() => {
    const stored = localStorage.getItem(STORAGE_KEY);
    return isValidCampus(stored) ? stored : DEFAULT_CAMPUS;
  });

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, slug);
  }, [slug]);

  const patientService = useMemo(() => createPatientService(slug), [slug]);

  const tenant = useMemo(() => {
    const c = CAMPUSES[slug];
    return {
      id: slug,
      label: c?.label ?? slug,
      features: TENANT_FEATURES[slug] ?? {},
    };
  }, [slug]);

  const value = useMemo(
    () => ({
      tenant,
      patientService,
      campus: slug,
      /** v1 components imported `db` from useTenant() — v2 exposes `campus` here too so the
       *  generatedNotePresence helpers can be called as `getNotesAddedYesNo(db /* campus *\/, …)`. */
      db: slug,
      changeCampus: (next) => {
        if (isValidCampus(next)) setSlug(next);
      },
    }),
    [tenant, patientService, slug],
  );

  return <TenantContext.Provider value={value}>{children}</TenantContext.Provider>;
}

export function useTenant() {
  const ctx = useContext(TenantContext);
  if (!ctx) throw new Error("useTenant must be used inside TenantProvider");
  return ctx;
}
